import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../state/auth';
import { useToast } from '../state/toast';

interface UserHit {
  id: string;
  username: string;
  display_name: string | null;
  color: string;
}

interface BanRow {
  user_id: string;
  banned_until: string;
  display_name: string;
  username: string;
}

const DURATIONS: Array<{ label: string; minutes: number }> = [
  { label: '10 分钟', minutes: 10 },
  { label: '1 小时', minutes: 60 },
  { label: '1 天', minutes: 60 * 24 },
  { label: '7 天', minutes: 60 * 24 * 7 },
  { label: '30 天', minutes: 60 * 24 * 30 },
  { label: '永久', minutes: 0 },
];

function fmtUntil(iso: string): string {
  const d = new Date(iso);
  if (d.getFullYear() > 2100) return '永久';
  return d.toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtRemaining(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return '已过期';
  if (new Date(iso).getFullYear() > 2100) return '—';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m} 分钟`;
  if (m < 60 * 24) return `${Math.floor(m / 60)} 小时`;
  return `${Math.floor(m / 60 / 24)} 天`;
}

export function BanManager() {
  const token = useAuth((s) => s.token);
  const pushToast = useToast((s) => s.push);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<UserHit[]>([]);
  const [picked, setPicked] = useState<UserHit | null>(null);
  const [duration, setDuration] = useState(60);
  const [bans, setBans] = useState<BanRow[]>([]);
  const [busy, setBusy] = useState(false);
  const searchTimer = useRef<number | null>(null);

  const loadBans = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch('/api/admin/bans', { headers: { authorization: `Bearer ${token}` } });
      if (r.ok) setBans(await r.json());
    } catch { /* 顶部会有其他错误提示 */ }
  }, [token]);

  useEffect(() => { void loadBans(); }, [loadBans]);

  // 防抖搜索：昵称 / 用户名 / user_id（支持 C 端悬停看到的 #前缀）
  useEffect(() => {
    if (searchTimer.current != null) clearTimeout(searchTimer.current);
    const v = q.trim();
    if (!v) { setHits([]); return; }
    if (!token) return;
    searchTimer.current = window.setTimeout(async () => {
      try {
        const r = await fetch(`/api/admin/users/search?q=${encodeURIComponent(v.replace(/^#/, ''))}`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (r.ok) setHits(await r.json());
      } catch { /* ignore */ }
    }, 250);
    return () => { if (searchTimer.current != null) clearTimeout(searchTimer.current); };
  }, [q, token]);

  async function ban() {
    if (!picked || !token) return;
    setBusy(true);
    try {
      const r = await fetch('/api/admin/bans', {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ user_id: picked.id, minutes: duration }),
      });
      if (!r.ok) { pushToast('error', '封禁失败，请重试。'); return; }
      pushToast('success', `已封禁 ${picked.display_name ?? picked.username}（${DURATIONS.find((d) => d.minutes === duration)?.label}）。对方在线会立即收到提示。`);
      setPicked(null);
      setQ('');
      setHits([]);
      await loadBans();
    } finally {
      setBusy(false);
    }
  }

  async function unban(userId: string) {
    if (!token) return;
    const r = await fetch(`/api/admin/bans/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers: { authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      pushToast('info', '已解封。对方刷新画板后即可继续涂鸦。', 3000);
      await loadBans();
    }
  }

  return (
    <section className="rounded-2xl bg-white border border-brand-100 p-6 shadow-elev1">
      <h3 className="text-[15px] font-semibold mb-1">封禁管理</h3>
      <p className="text-[13px] text-neutral-500 mb-4">
        被封禁的用户不能落笔和擦除（仍可围观），在线时会立即收到提示。可按昵称、用户名或用户 ID（画板上悬停笔画显示的 # 开头编号）搜索。
      </p>

      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[260px] relative">
          <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 mb-1">
            搜索用户
          </label>
          <input
            value={picked ? `${picked.display_name ?? picked.username}（${picked.id.slice(0, 8)}…）` : q}
            onChange={(e) => { setPicked(null); setQ(e.target.value); }}
            placeholder="昵称 / 用户名 / #066e022d…"
            className="w-full h-10 px-3 rounded-xl border border-neutral-200 bg-white text-sm outline-none focus:border-brand-500"
            aria-label="搜索用户"
          />
          {!picked && hits.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-neutral-200 bg-white shadow-elev2 text-sm">
              {hits.map((h) => (
                <li key={h.id}>
                  <button
                    onClick={() => { setPicked(h); setHits([]); }}
                    className="w-full text-left px-3 py-2 hover:bg-brand-50 flex items-center gap-2"
                  >
                    <span className="w-2.5 h-2.5 rounded-full flex-none" style={{ background: h.color }} />
                    <span className="font-medium">{h.display_name ?? h.username}</span>
                    <span className="text-neutral-400 text-xs truncate">{h.id.slice(0, 18)}…</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 mb-1">
            时长
          </label>
          <select
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value, 10))}
            className="h-10 px-3 rounded-xl border border-neutral-200 bg-white text-sm outline-none"
            aria-label="封禁时长"
          >
            {DURATIONS.map((d) => (
              <option key={d.minutes} value={d.minutes}>{d.label}</option>
            ))}
          </select>
        </div>
        <div className="pt-[26px]">
          <button
            onClick={ban}
            disabled={!picked || busy}
            className="h-10 px-4 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {busy ? '执行中…' : '封禁'}
          </button>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-[11px] font-medium uppercase tracking-wider text-neutral-500 mb-2">
          生效中的封禁（{bans.length}）
        </div>
        {bans.length === 0 ? (
          <p className="text-sm text-neutral-400">当前没有生效中的封禁。</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[12px] text-neutral-500 border-b border-neutral-100">
                <th className="py-2 font-normal">用户</th>
                <th className="py-2 font-normal">ID</th>
                <th className="py-2 font-normal">封禁至</th>
                <th className="py-2 font-normal">剩余</th>
                <th className="py-2 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {bans.map((b) => (
                <tr key={b.user_id} className="border-b border-neutral-50">
                  <td className="py-2">{b.display_name}</td>
                  <td className="py-2 font-mono text-xs text-neutral-400">{b.user_id.slice(0, 18)}…</td>
                  <td className="py-2">{fmtUntil(b.banned_until)}</td>
                  <td className="py-2 text-neutral-500">{fmtRemaining(b.banned_until)}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => unban(b.user_id)}
                      className="h-8 px-3 rounded-lg text-xs font-medium text-white bg-brand-600 hover:bg-brand-700"
                    >
                      解封
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
